/**
 * The style panel (KHIYARSA-EDITOR-1 Phase 5, §B3).
 *
 * Presets are a shortcut, never the model: every genuinely supported property
 * has its own control here, and anything the text cannot express is either
 * absent or disabled with the reason shown. Nothing in this component holds
 * state — it reads the document and writes the document.
 */
import React, { useState } from "react";

import {
  BUILTIN_TAGS,
  DIRECTIONS,
  NAMED_COLORS,
  NODE_KINDS,
  OPEN_COLOR_SCALE,
  ROUTINGS,
  SPACINGS,
  STYLE_KEYS,
  THEMES,
} from "../vocab";

import {
  PRESETS,
  applyPreset,
  frontmatterAvailability,
  isColorKey,
  keyAvailability,
  parseStyle,
  readFrontmatter,
  setFrontmatterKey,
  setLineKey,
  setLineKind,
  targetAt,
  toggleLineTag,
} from "./style";
import { useEditorT } from "./strings";

import type { Family } from "../types";

export interface KmdStylePanelProps {
  text: string;
  caret: number;
  family: Family;
  onChange: (text: string) => void;
}

const HEX_RE = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** palette swatches plus an exact-colour field — both first class (Q4) */
const ColorControl = ({
  value,
  onPick,
  onClear,
}: {
  value?: string;
  onPick: (token: string) => void;
  onClear: () => void;
}) => {
  const t = useEditorT();
  const [custom, setCustom] = useState(
    value && HEX_RE.test(value) ? value : "",
  );
  const isCustom = Boolean(value && HEX_RE.test(value));

  return (
    <div className="kmd-style__color">
      <div className="kmd-style__swatches">
        {Object.entries(OPEN_COLOR_SCALE).map(([name, scale]) => (
          <div key={name} className="kmd-style__swatch-row">
            {[1, 3, 5, 7, 9].map((weight) => (
              <button
                key={weight}
                className={`kmd-style__swatch${
                  value === `${name}.${weight}` ? " is-active" : ""
                }`}
                style={{ background: scale[weight] }}
                title={`${name}.${weight}`}
                onClick={() => onPick(`${name}.${weight}`)}
              />
            ))}
          </div>
        ))}
        <div className="kmd-style__swatch-row">
          {Object.keys(NAMED_COLORS).map((name) => (
            <button
              key={name}
              className={`kmd-style__swatch kmd-style__swatch--named${
                value === name ? " is-active" : ""
              }`}
              style={{
                background:
                  name === "transparent" ? "transparent" : NAMED_COLORS[name],
              }}
              title={name}
              onClick={() => onPick(name)}
            />
          ))}
        </div>
      </div>

      <div className="kmd-style__custom">
        <label>{t("customColor")}</label>
        <input
          type="color"
          value={isCustom && custom.length === 7 ? custom : "#1a73e8"}
          onChange={(e) => {
            setCustom(e.target.value);
            onPick(e.target.value);
          }}
        />
        <input
          className="kmd-style__hex"
          type="text"
          spellCheck={false}
          placeholder="#RRGGBB"
          value={custom}
          onChange={(e) => {
            const next = e.target.value.trim();
            setCustom(next);
            if (HEX_RE.test(next)) {
              onPick(next);
            }
          }}
        />
        {custom && !HEX_RE.test(custom) && (
          <span className="kmd-style__hex-error">{t("badHex")}</span>
        )}
        {value && (
          <button className="kmd-style__clear" onClick={onClear}>
            {t("clearValue")}
          </button>
        )}
      </div>
    </div>
  );
};

const Row = ({
  label,
  disabledReason,
  children,
}: {
  label: string;
  disabledReason?: string;
  children: React.ReactNode;
}) => {
  const t = useEditorT();
  return (
    <div className={`kmd-style__row${disabledReason ? " is-disabled" : ""}`}>
      <span className="kmd-style__label">{label}</span>
      <div className="kmd-style__control">
        {disabledReason ? (
          <span className="kmd-style__reason">
            {t(`why_${disabledReason}` as never)}
          </span>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export const KmdStylePanel = ({
  text,
  caret,
  family,
  onChange,
}: KmdStylePanelProps) => {
  const t = useEditorT();
  const [openColor, setOpenColor] = useState<string | null>(null);

  const target = targetAt(text, caret, family);
  const style = parseStyle(target.attrs);
  const fm = readFrontmatter(text);
  const editable = target.kind !== "frontmatter" && target.kind !== "other";

  const setKey = (key: string, value: string | null) =>
    onChange(setLineKey(text, caret, family, key, value));

  const enumRow = (key: string) => {
    const allowed = STYLE_KEYS[key];
    if (allowed === "*" || !allowed) {
      return null;
    }
    const availability = keyAvailability(key, target, family);
    return (
      <Row
        key={key}
        label={t(`style_${key}` as never)}
        disabledReason={availability.enabled ? undefined : availability.reason}
      >
        <div className="kmd-style__chips">
          {allowed.map((option) => (
            <button
              key={option}
              className={`kmd-style__chip${
                style.keys[key] === option ? " is-active" : ""
              }`}
              onClick={() =>
                setKey(key, style.keys[key] === option ? null : option)
              }
            >
              {option}
            </button>
          ))}
        </div>
      </Row>
    );
  };

  return (
    <div className="kmd-style">
      {/* ---- document ------------------------------------------------- */}
      <section>
        <h4>{t("styleDocument")}</h4>

        <Row label={t("stylePreset")}>
          <div className="kmd-style__chips">
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                className="kmd-style__chip"
                onClick={() => onChange(applyPreset(text, preset))}
              >
                {t(`preset_${preset.id}` as never)}
              </button>
            ))}
          </div>
        </Row>

        {(
          [
            ["theme", THEMES],
            ["spacing", SPACINGS],
            ["direction", DIRECTIONS],
            ["routing", ROUTINGS],
          ] as const
        ).map(([key, options]) => {
          const availability = frontmatterAvailability(key, family);
          return (
            <Row
              key={key}
              label={t(`fm_${key}` as never)}
              disabledReason={
                availability.enabled ? undefined : availability.reason
              }
            >
              <div className="kmd-style__chips">
                {options.map((option) => (
                  <button
                    key={option}
                    className={`kmd-style__chip${
                      fm[key] === option ? " is-active" : ""
                    }`}
                    onClick={() =>
                      onChange(
                        setFrontmatterKey(
                          text,
                          key,
                          fm[key] === option ? null : option,
                        ),
                      )
                    }
                  >
                    {option}
                  </button>
                ))}
              </div>
            </Row>
          );
        })}
      </section>

      {/* ---- selection ------------------------------------------------ */}
      <section>
        <h4>
          {t("styleSelection")}{" "}
          <span className="kmd-style__target">
            {editable ? target.label || t(`kind_${target.kind}` as never) : ""}
          </span>
        </h4>

        {!editable ? (
          <p className="kmd-style__reason">{t("why_noTarget")}</p>
        ) : (
          <>
            {target.kind === "node" && (
              <Row label={t("style_kind")}>
                <div className="kmd-style__chips">
                  {NODE_KINDS.map((kind) => (
                    <button
                      key={kind}
                      className={`kmd-style__chip${
                        style.kind === kind ? " is-active" : ""
                      }`}
                      onClick={() =>
                        onChange(
                          setLineKind(
                            text,
                            caret,
                            family,
                            style.kind === kind ? null : kind,
                          ),
                        )
                      }
                    >
                      {kind}
                    </button>
                  ))}
                </div>
              </Row>
            )}

            <Row label={t("style_tags")}>
              <div className="kmd-style__chips">
                {BUILTIN_TAGS.filter((tag) => tag !== "unresolved").map(
                  (tag) => (
                    <button
                      key={tag}
                      className={`kmd-style__chip${
                        style.tags.includes(tag) ? " is-active" : ""
                      }`}
                      onClick={() =>
                        onChange(toggleLineTag(text, caret, family, tag))
                      }
                    >
                      .{tag}
                    </button>
                  ),
                )}
              </div>
            </Row>

            {Object.keys(STYLE_KEYS)
              .filter(isColorKey)
              .map((key) => {
                const availability = keyAvailability(key, target, family);
                return (
                  <Row
                    key={key}
                    label={t(`style_${key}` as never)}
                    disabledReason={
                      availability.enabled ? undefined : availability.reason
                    }
                  >
                    <button
                      className="kmd-style__chip"
                      onClick={() =>
                        setOpenColor(openColor === key ? null : key)
                      }
                    >
                      {style.keys[key] ?? t("pick")}
                    </button>
                    {openColor === key && (
                      <ColorControl
                        value={style.keys[key]}
                        onPick={(token) => setKey(key, token)}
                        onClear={() => setKey(key, null)}
                      />
                    )}
                  </Row>
                );
              })}

            {Object.keys(STYLE_KEYS)
              .filter((key) => !isColorKey(key) && key !== "opacity")
              .map(enumRow)}

            <Row
              label={t("style_opacity")}
              disabledReason={
                keyAvailability("opacity", target, family).enabled
                  ? undefined
                  : keyAvailability("opacity", target, family).reason
              }
            >
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={Number(style.keys.opacity ?? 100)}
                onChange={(e) =>
                  setKey(
                    "opacity",
                    e.target.value === "100" ? null : e.target.value,
                  )
                }
              />
              <span className="kmd-style__value">
                {style.keys.opacity ?? 100}
              </span>
            </Row>
          </>
        )}
      </section>
    </div>
  );
};
